<template>
  <div
    v-if="showGate"
    class="collection-gate"
    :style="{
      '--backgroundImageSmall': `url(${backgroundImages?.smallMedia})`,
      '--backgroundImageMedium': `url(${backgroundImages?.mediumMedia})`,
      '--backgroundImageLarge': `url(${backgroundImages?.largeMedia})`,
    }"
  >
    <IconButton
      theme="white"
      icon="cross"
      class="close-btn"
      @click="redirectToHomepage"
    />
    <div class="collection-gate__content">
      <div class="collection-gate__background">
        <div class="background-image"></div>
        <div class="overlay"></div>
      </div>
      <div class="collection-gate__mobile-background">
        <Media
          :images="images"
          cover
          class="media"
          cover-position="center center"
        />
        <div class="overlay"></div>
      </div>
      <div class="collection-gate__content-inner">
        <CollectionGateOpening
          v-if="showLandingOpening"
          :end-date="endDate"
          @login="handleShowAccountForm('login')"
          @register="handleShowAccountForm('register')"
        />
        <template v-else-if="shownAccountForm">
          <LandingLoginForm
            v-if="shownAccountForm === 'login'"
            :return-url="`/collections/${collectionHandle}`"
            :hide-register-link="true"
            @success="handleSuccess"
            @register="shownAccountForm = 'register'"
          />
        </template>
        <CollectionGateSuccess
          v-else-if="showGateSuccess"
          :end-date="endDate"
        />
      </div>
    </div>
    <div class="collection-gate__images">
      <div class="video-container">
        <Media
          :images="images"
          cover
          class="media"
          cover-position="top center"
        />
      </div>
      <div class="overlay"></div>
    </div>
  </div>
</template>

<script>
import LandingLoginForm from "@/components/landing/LandingLoginForm.vue";
import CollectionGateSuccess from "@/components/collection/CollectionGateSuccess.vue";
import CollectionGateOpening from "@/components/collection/CollectionGateOpening.vue";
import IconButton from "@/components/IconButton.vue";
import Media from "@/components/Media.vue";
import { mapGetters, mapState } from "vuex";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import {
  collectionGateAccessTimes,
  getCollectionGateTierLevel,
  getCollectionGateAccessTime,
} from "@/config.js";

export default {
  name: "CollectionGate",
  components: {
    LandingLoginForm,
    CollectionGateSuccess,
    CollectionGateOpening,
    IconButton,
    Media,
  },
  inject: ["mq"],
  props: {
    collectionHandle: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      success: false,
      shownAccountForm: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["isLoggedIn"]),
    ...mapState("auth", ["customer"]),
    ...mapGetters("app", ["currencyCode"]),
    showGate() {
      const { collectionGateEnabled = true } = getFeatureToggles();

      // Check if gate is enabled and if this is the correct collection
      if (!collectionGateEnabled) {
        return false;
      }

      if (this.collectionHandle !== "the-attimi-capsule") {
        return false;
      }

      // Check if user has already viewed the collection
      const storageKey = `collection_access_${this.collectionHandle}`;
      const hasViewedCollection = localStorage.getItem(storageKey) === "true";

      if (hasViewedCollection) {
        return false;
      }

      // Check for all access tag
      const userTags = this.customer?.tags || [];

      if (userTags.includes("allaccess")) {
        // Store that they've viewed the collection
        localStorage.setItem(storageKey, "true");
        return false;
      }

      // Get the current currency's access times
      const currencyAccessTimes =
        collectionGateAccessTimes[this.currencyCode.toLowerCase()] ||
        collectionGateAccessTimes.gbp;

      if (!currencyAccessTimes) {
        console.warn("No access times found for currency:", this.currencyCode);
        return false;
      }

      const now = new Date();
      const disabledTime = new Date(currencyAccessTimes.disabled);

      // If we've passed the Everyone time, gate is off
      if (now >= disabledTime) {
        return false;
      }

      const tierLevel = getCollectionGateTierLevel(userTags);
      const accessTimeIso = getCollectionGateAccessTime(
        currencyAccessTimes,
        tierLevel
      );

      // Tiered members may pass the gate once their access window opens
      if (tierLevel > 0 && accessTimeIso && now >= new Date(accessTimeIso)) {
        return false;
      }

      // Show the gate for everyone else
      return true;
    },
    endDate() {
      const currencyAccessTimes =
        collectionGateAccessTimes[this.currencyCode.toLowerCase()] ||
        collectionGateAccessTimes.gbp;
      if (!currencyAccessTimes) {
        return null;
      }

      const tierLevel = getCollectionGateTierLevel(this.customer?.tags || []);
      return getCollectionGateAccessTime(currencyAccessTimes, tierLevel);
    },
    showLandingOpening() {
      if (this.success || this.showGateSuccess) {
        return false;
      }
      return this.shownAccountForm === null;
    },
    showGateSuccess() {
      if (!this.isLoggedIn) {
        return false;
      }

      const currencyAccessTimes =
        collectionGateAccessTimes[this.currencyCode.toLowerCase()] ||
        collectionGateAccessTimes.gbp;
      if (!currencyAccessTimes) {
        return false;
      }

      const now = new Date();
      const disabledTime = new Date(currencyAccessTimes.disabled);
      const tierLevel = getCollectionGateTierLevel(this.customer?.tags || []);
      const accessTimeIso = getCollectionGateAccessTime(
        currencyAccessTimes,
        tierLevel
      );
      const accessTime = accessTimeIso ? new Date(accessTimeIso) : disabledTime;

      // After Everyone opens, don't show the success/gate state
      if (now >= disabledTime) {
        return false;
      }

      // Tiered members: countdown before access, then success until Everyone
      if (tierLevel > 0) {
        if (now < accessTime) {
          return true;
        }
        return now >= accessTime && now < disabledTime;
      }

      // Non-tier members: countdown to Everyone
      return now < disabledTime;
    },
    userTierLevel() {
      return getCollectionGateTierLevel(this.customer?.tags || []);
    },
    images() {
      return {
        ssmallMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_8b20c99b-7705-4f57-906a-a61f9a35ece7.jpg?v=1792489245",
        mediumMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_8b20c99b-7705-4f57-906a-a61f9a35ece7.jpg?v=1792489245",
        largeMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_6.jpg?v=1792489238",
        largeMedia2x:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_6.jpg?v=1792489238",
      };
    },
    backgroundImages() {
      return {
        smallMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_8b20c99b-7705-4f57-906a-a61f9a35ece7.jpg?v=1792489245",
        mediumMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_8b20c99b-7705-4f57-906a-a61f9a35ece7.jpg?v=1792489245",
        largeMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_4_2dc057f0-fe2b-4d88-8a19-5f33d3affa74.jpg?v=1792489236",
      };
    },
  },
  methods: {
    handleShowAccountForm(form) {
      this.shownAccountForm = form;
    },
    handleSuccess() {
      this.success = true;
      this.shownAccountForm = null;

      // Store that user has successfully accessed this collection
      localStorage.setItem(
        `collection_access_${this.collectionHandle}`,
        "true"
      );
    },
    redirectToHomepage() {
      window.location.href = "/";
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";

.collection-gate {
  position: fixed;
  z-index: 1000;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: transparent;
  display: flex;
  flex-direction: column-reverse;
  overflow: hidden;

  @include respond-above(md) {
    flex-direction: row;
    height: auto;
    background-color: transparent;
  }

  &__content,
  &__images {
    flex: 0 0 50%;
  }

  .close-btn {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 20;
    padding: 8px;
    border-radius: 50%;
    background-color: transparent;

    &:deep(.icon) {
      color: $color-white;
    }
  }
}

.collection-gate__content {
  color: $color-white;
  height: 100vh;
  position: relative;
  overflow: visible;
  padding: 0;
  margin: 0;
  background-image: none;
  background-color: transparent;
  flex: 0 0 50%;
  order: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  @include respond-above(md) {
    padding-top: 0;
    background-image: none;
    border-right: none;
  }

  .collection-gate__background {
    display: none;

    @include respond-above(md) {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 0;
    }

    .background-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: url("https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_8b20c99b-7705-4f57-906a-a61f9a35ece7.jpg?v=1792489245");
      background-size: cover;
      background-position: center;
    }

    .overlay {
      position: absolute;
      content: "";
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      // background: linear-gradient(
      //   180deg,
      //   rgba(0, 0, 0, 0.3) 0%,
      //   rgba(0, 0, 0, 0.7) 0%
      // );
      z-index: 1;
    }
  }

  .collection-gate__mobile-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 0;

    .media {
      height: 100%;
      width: 100%;
      position: relative;
    }

    .overlay {
      position: absolute;
      content: "";
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      // background: linear-gradient(
      //   180deg,
      //   rgba(0, 0, 0, 0.3) 0%,
      //   rgba(0, 0, 0, 0.7) 100%
      // );
      z-index: 1;
    }

    @include respond-above(md) {
      display: none;
    }
  }

  &::before {
    display: none;
  }
}

.collection-gate__content-inner {
  position: relative;
  z-index: 3;
  text-align: center;
  padding: 20px;
  max-width: 480px;
  width: 100%;
  margin: 0 auto;

  @include respond-below(md) {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 380px;
  }

  @include respond-above(md) {
    padding: 40px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);
  }
}

.collection-gate__images {
  display: none;
  background-color: $color-black;
  position: relative;
  overflow: hidden;

  @include respond-above(md) {
    display: block;
    flex: 0 0 50%;
    order: 2;
    height: 100vh;
  }

  .video-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .media {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.15);
    z-index: 5;
  }
}

.collection-gate__video-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;

  &::before {
    position: absolute;
    content: "";
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba($color-black, 0.7);
    z-index: 1;
  }

  .image {
    height: 100%;
    width: 100%;
    object-fit: cover;
    position: relative;
    z-index: 0;
  }

  @include respond-above(md) {
    display: none;
  }
}

.media {
  height: 100%;
  width: 100%;

  &:deep(video) {
    height: 100%;
    width: 100%;
    object-fit: cover;
    object-position: center;
  }
}
</style>
